import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
    Badge,
    Button,
    Combobox,
    ComboboxChip,
    ComboboxChips,
    ComboboxChipsInput,
    ComboboxCollection,
    ComboboxContent,
    ComboboxItem,
    ComboboxList,
    ComboboxTrigger,
    ComboboxValue,
    type ComboboxFieldOption,
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Field,
    FieldLabel,
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
    useComboboxAnchor,
} from '@campai/lib-web/components/new/ui'
import { client, type RouterOutput } from '@campai/lib-web/utils/trpc'

import { STATUS_OPTIONS, getStatusLabel, type Status } from '@/product/feedback/status'

type MetadataDialogProps = {
    open: boolean
    ticket: TicketData
    onOpenChange: (open: boolean) => void
    onSubmit: (input: { status: Status; tags: string[] }) => Promise<void>
}

type TicketData = RouterOutput['product']['feedback']['getTicket']

const isStatus = (value: unknown): value is Status => {
    return STATUS_OPTIONS.some((status) => status === value)
}

const normalizeTag = (tag: string) => tag.trim()
const toTagKey = (tag: string) => normalizeTag(tag).toLowerCase()

const normalizeTags = (tags: string[]) => {
    const normalizedTags: string[] = []
    const normalizedTagSet = new Set<string>()

    tags.forEach((tag) => {
        const normalizedTag = normalizeTag(tag)
        const normalizedTagKey = toTagKey(normalizedTag)

        if (!normalizedTag || normalizedTagSet.has(normalizedTagKey)) {
            return
        }

        normalizedTags.push(normalizedTag)
        normalizedTagSet.add(normalizedTagKey)
    })

    return normalizedTags
}

const toTagOptions = (tags: string[]): ComboboxFieldOption[] => tags.map((tag) => ({ label: tag, value: tag }))

export const MetadataDialog = ({ open, ticket, onOpenChange, onSubmit }: MetadataDialogProps) => {
    const { t } = useTranslation()
    const tagsAnchor = useComboboxAnchor()
    const [status, setStatus] = useState<Status>(ticket.status)
    const [tags, setTags] = useState<string[]>([])
    const [tagQuery, setTagQuery] = useState('')
    const [tagOptions, setTagOptions] = useState<ComboboxFieldOption[]>([])
    const [tagComboboxOpen, setTagComboboxOpen] = useState(false)
    const [areTagOptionsLoading, setAreTagOptionsLoading] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)

    useEffect(() => {
        if (!open) {
            return
        }

        setStatus(ticket.status)
        setTags(ticket.tags)
        setTagQuery('')
        setTagOptions([])
        setAreTagOptionsLoading(false)
        setIsSubmitting(false)
    }, [open, ticket])

    const renderTagOption = useCallback((option: ComboboxFieldOption) => {
        return (
            <Badge variant="colored" seed={option.value.toLowerCase()}>
                {option.label}
            </Badge>
        )
    }, [])

    const selectedTagOptions = useMemo(() => toTagOptions(tags), [tags])
    const availableTagOptions = useMemo(
        () =>
            [...selectedTagOptions, ...tagOptions].filter(
                (option, index, options) =>
                    options.findIndex((candidate) => toTagKey(candidate.value) === toTagKey(option.value)) === index
            ),
        [selectedTagOptions, tagOptions]
    )
    const displayedTagOptions = useMemo(() => {
        const normalizedTagQuery = normalizeTag(tagQuery)

        if (
            !normalizedTagQuery ||
            availableTagOptions.some((option) => toTagKey(option.value) === toTagKey(normalizedTagQuery))
        ) {
            return availableTagOptions
        }

        return [...availableTagOptions, { label: normalizedTagQuery, value: normalizedTagQuery }]
    }, [availableTagOptions, tagQuery])

    useEffect(() => {
        if (!open || !tagComboboxOpen) {
            return
        }

        const controller = new AbortController()
        setAreTagOptionsLoading(true)
        const timeout = window.setTimeout(async () => {
            try {
                const result = await client.product.feedback.listTags.query(
                    {
                        searchTerm: tagQuery,
                        excludeTags: tags.length ? tags : undefined,
                        offset: 0,
                        limit: 20,
                        returnCount: false,
                    },
                    { signal: controller.signal }
                )

                setTagOptions(toTagOptions(result.tags))
            } catch (error) {
                if (!controller.signal.aborted) {
                    console.error(error)
                }
            } finally {
                if (!controller.signal.aborted) {
                    setAreTagOptionsLoading(false)
                }
            }
        }, 250)

        return () => {
            window.clearTimeout(timeout)
            controller.abort()
        }
    }, [open, tagComboboxOpen, tagQuery, tags])

    const addCustomTags = (value: string) => {
        const newTags = normalizeTags(value.split(','))

        if (newTags.length === 0) {
            return
        }

        setTags((currentTags) => normalizeTags([...currentTags, ...newTags]))
        setTagQuery('')
    }

    const handleSubmit = async () => {
        if (isSubmitting) {
            return
        }

        setIsSubmitting(true)

        try {
            await onSubmit({ status, tags: normalizeTags([...tags, ...tagQuery.split(',')]) })
            onOpenChange(false)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>{t('product.feedback.ticket.metadataDialog.title')}</DialogTitle>
                    <DialogDescription>{t('product.feedback.ticket.metadataDialog.description')}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <Field>
                        <FieldLabel>{t('product.feedback.ticket.status')}</FieldLabel>
                        <Select
                            value={status}
                            onValueChange={(value) => {
                                if (isStatus(value)) {
                                    setStatus(value)
                                }
                            }}
                        >
                            <SelectTrigger className="bg-background w-40">
                                <SelectValue>{getStatusLabel(status)}</SelectValue>
                            </SelectTrigger>
                            <SelectContent align="start">
                                <SelectGroup>
                                    {STATUS_OPTIONS.map((statusOption) => (
                                        <SelectItem key={statusOption} value={statusOption}>
                                            {getStatusLabel(statusOption)}
                                        </SelectItem>
                                    ))}
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field>
                        <FieldLabel>{t('product.feedback.ticket.metadataDialog.tags')}</FieldLabel>
                        <Combobox<ComboboxFieldOption, true>
                            multiple
                            items={displayedTagOptions}
                            value={selectedTagOptions}
                            inputValue={tagQuery}
                            filter={null}
                            autoHighlight
                            isItemEqualToValue={(option, selectedOption) =>
                                toTagKey(option.value) === toTagKey(selectedOption.value)
                            }
                            itemToStringValue={(option) => option.value}
                            itemToStringLabel={(option) => option.label}
                            onOpenChange={setTagComboboxOpen}
                            onInputValueChange={setTagQuery}
                            onValueChange={(options) => {
                                setTags(normalizeTags(options.map((option) => option.value)))
                                setTagQuery('')
                            }}
                        >
                            <ComboboxChips ref={tagsAnchor} className="w-full">
                                <ComboboxValue>
                                    {(options: ComboboxFieldOption[]) => (
                                        <>
                                            {options.map((option) => (
                                                <ComboboxChip key={option.value}>{option.label}</ComboboxChip>
                                            ))}
                                            <ComboboxChipsInput
                                                placeholder={
                                                    options.length === 0
                                                        ? t('product.feedback.ticket.metadataDialog.tagsPlaceholder')
                                                        : undefined
                                                }
                                                onKeyDown={(event) => {
                                                    if (
                                                        (event.key === 'Enter' || event.key === ',') &&
                                                        tagQuery.trim() &&
                                                        !(
                                                            event.key === 'Enter' &&
                                                            availableTagOptions.some(
                                                                (option) =>
                                                                    toTagKey(option.value) === toTagKey(tagQuery)
                                                            )
                                                        )
                                                    ) {
                                                        event.preventDefault()
                                                        addCustomTags(tagQuery)
                                                    }
                                                }}
                                            />
                                        </>
                                    )}
                                </ComboboxValue>
                                <ComboboxTrigger
                                    render={<Button type="button" variant="ghost" size="icon-xs" />}
                                />
                            </ComboboxChips>
                            <ComboboxContent anchor={tagsAnchor}>
                                <ComboboxList loading={areTagOptionsLoading}>
                                    <ComboboxCollection>
                                        {(option: ComboboxFieldOption) => (
                                            <ComboboxItem key={option.value} value={option}>
                                                {renderTagOption(option)}
                                            </ComboboxItem>
                                        )}
                                    </ComboboxCollection>
                                </ComboboxList>
                            </ComboboxContent>
                        </Combobox>
                    </Field>
                </div>
                <DialogFooter>
                    <DialogClose
                        render={
                            <Button variant="outline" disabled={isSubmitting}>
                                {t('common.cancel')}
                            </Button>
                        }
                    />
                    <Button loading={isSubmitting} onClick={() => void handleSubmit()}>
                        {t('common.save')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
